import {
  calculateShiftDurationSafe,
  calculateShiftBreakDurationSafe,
  calculateShiftNetHoursSafe,
  formatDuration,
  hasDisplayableShiftData,
  hoursHaveChanged,
  ShiftShape,
} from "common/utils/timesheet";
/**
 * Core component that works with new ShiftData format
 * Handles null start/end times gracefully
 */
const ShiftDetailTotal = ({ shift }) => {
  // If we don't have enough data to display anything meaningful
  if (!hasDisplayableShiftData(shift)) {
    return (
      <div className="py-3 border-t border-neutral-20">
        <div className="flex items-center justify-between">
          <span className="body-2 font-bold text-neutral-80">Shift totals</span>
          <span className="body-2 italic text-neutral-60">
            Enter start and end times
          </span>
        </div>
      </div>
    );
  }

  // Calculate the components using safe utilities that handle null values
  const shiftDuration = calculateShiftDurationSafe(shift);
  const breakDuration = calculateShiftBreakDurationSafe(shift);
  const calculatedNetHours = calculateShiftNetHoursSafe(shift);

  // Determine what to display
  const hasOverride = shift.hoursWorkedEmployer != null;
  const displayNetHours = hasOverride
    ? shift.hoursWorkedEmployer
    : calculatedNetHours;

  // Show employee's original value as strikethrough when manager changed it
  const shouldShowStrikethrough =
    hasOverride &&
    !shift.createdByManager &&
    hoursHaveChanged(calculatedNetHours, displayNetHours);

  // Show breakdown if there are breaks or differences to explain
  const hasBreaks =
    shift.breaks.length > 0 || shift.hoursBreakEmployee !== undefined;
  const showBreakdown = hasBreaks || hasOverride;
  return (
    <div className="mt-4 border-t border-neutral-20 py-4">
      {/* Shift Duration (total time at work) */}
      <div className="flex items-center justify-between">
        <span className="body-2 font-bold text-neutral-80">Shift duration</span>
        <span className="body-2 font-bold text-neutral-90">
          {formatDuration(shiftDuration * 3600)}
        </span>
      </div>

      {/* Breakdown - show breaks and net paid hours */}
      {showBreakdown && (
        <div className="body-2 mt-2 flex items-center justify-between text-neutral-60">
          <div className="flex flex-col gap-1">
            {hasBreaks && <span>Breaks (unpaid)</span>}
            <span className="font-bold text-neutral-80">Net paid hours</span>
          </div>
          <div className="flex flex-col gap-1 text-right">
            {hasBreaks && <span>-{formatDuration(breakDuration * 3600)}</span>}
            <span className="font-bold text-neutral-90">
              {shouldShowStrikethrough && (
                <span className="text-red-80 line-through">
                  {formatDuration(calculatedNetHours * 3600)}
                </span>
              )}
              {shouldShowStrikethrough && " "}
              {formatDuration(displayNetHours * 3600)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

ShiftDetailTotal.propTypes = {
  shift: ShiftShape.isRequired,
};

export { ShiftDetailTotal };
